import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, Edit2, Search, Users } from 'lucide-react';
import { primaryButton, ghostButton, inputStyle, glass, glassLight, T, Badge, StatCard, EmptyState, Field, usePagination, Pagination, useDesplazarAlAbrir } from '../ui';
interface Cliente {
  id: string;
  codigo: string;
  nombre: string;
  rnc: string;
  contacto: string;
  telefono: string;
  email: string;
  direccion: string;
  ubicacion: string;
  tipo: 'Industria' | 'Comercio' | 'Residencial' | 'Otro';
  capacidadTanque: number;
  estado: 'activo' | 'inactivo';
  observaciones: string;
  fecha_registro: string;
  ultima_compra?: string;
  dias_sin_comprar?: number;
  representante?: string;
  representanteCedula?: string;
  representanteDireccion?: string;
}
interface ClientesProps {
  clientes: Cliente[];
  onAddCliente: (cliente: Cliente) => void;
  onDeleteCliente: (id: string) => void;
  onUpdateCliente: (cliente: Cliente) => void;
}
const iconBtn: React.CSSProperties = {
  width: 30,
  height: 30,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 8,
  border: `1px solid ${T.border}`,
  background: T.surface,
  cursor: 'pointer'
};
export function Clientes({
  clientes,
  onAddCliente,
  onDeleteCliente,
  onUpdateCliente
}: ClientesProps) {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  // Al editar un registro del final del listado, el formulario se abre arriba:
  // sin llevar la vista hasta él parece que el botón no hizo nada.
  const refForm = useDesplazarAlAbrir(showForm, editingId);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEstado, setFilterEstado] = useState<'todos' | 'activo' | 'inactivo'>('todos');
  const [formData, setFormData] = useState<Partial<Cliente>>({
    tipo: 'Comercio',
    estado: 'activo',
    capacidadTanque: 1000
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.codigo || !formData.nombre || !formData.email) {
      alert('Por favor completa los campos obligatorios');
      return;
    }
    if (editingId) {
      onUpdateCliente({
        ...formData,
        id: editingId
      } as Cliente);
      setEditingId(null);
    } else {
      onAddCliente({
        ...formData,
        id: Date.now().toString(),
        fecha_registro: new Date().toISOString().split('T')[0]
      } as Cliente);
    }
    setFormData({
      tipo: 'Comercio',
      estado: 'activo',
      capacidadTanque: 1000
    });
    setShowForm(false);
  };
  const handleEdit = (cliente: Cliente) => {
    setFormData(cliente);
    setEditingId(cliente.id);
    setShowForm(true);
  };
  const filteredClientes = clientes.filter(c => {
    const matchSearch = c.codigo.toLowerCase().includes(searchTerm.toLowerCase()) || c.nombre.toLowerCase().includes(searchTerm.toLowerCase()) || c.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchEstado = filterEstado === 'todos' || c.estado === filterEstado;
    return matchSearch && matchEstado;
  });

  // Paginado: un listado largo obliga a hacer scroll para llegar a lo
  // último y hace la página pesada de dibujar.
  const {
    page,
    setPage,
    totalPages,
    pageItems,
    rangeStart,
    rangeEnd,
    total
  } = usePagination(filteredClientes, 10);
  const clientesActivos = clientes.filter(c => c.estado === 'activo').length;
  const clientesInactivos = clientes.filter(c => c.estado === 'inactivo').length;
  return <div style={{
    display: 'flex',
    flexDirection: 'column',
    gap: 20
  }} data-id="element-178" data-mp="30i6fhl2vw8if">
      {/* Header Stats */}
      <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
      gap: 14
    }} data-id="element-179" data-mp="2qqaeo29ey4ai">
        
        <StatCard label="Total clientes" value={String(clientes.length)} note="Registrados en el sistema" accent={T.info} data-id="element-180" data-mp="3efctz3gkrl1u" />
        
        <StatCard label="Activos" value={String(clientesActivos)} note="Comprando actualmente" accent={T.ok} data-id="element-181" data-mp="3j8o2d911c9vv" />
        
        <StatCard label="Inactivos" value={String(clientesInactivos)} note="Sin actividad reciente" accent={T.danger} data-id="element-182" data-mp="1qjoqfsxpci91" />
        
      </div>

      {/* Controles */}
      <div style={{
      ...glassLight,
      padding: 16
    }} data-id="element-183" data-mp="1lklsesg53cak">
        <div style={{
        display: 'flex',
        gap: 10,
        flexWrap: 'wrap',
        alignItems: 'center'
      }} data-id="element-184" data-mp="1gmalaph8bfw1">
          
          <div style={{
          flex: '1 1 240px',
          position: 'relative'
        }} data-id="element-185" data-mp="2eb3z03g2nvsq">
            <Search size={16} style={{
            position: 'absolute',
            left: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            color: T.textMuted
          }} data-id="element-186" data-mp="29c1070qoqlt7" />
            
            <input type="text" placeholder="Buscar por código, nombre o email..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{
            ...inputStyle,
            paddingLeft: 36
          }} data-id="element-187" data-mp="2wziounenl3ry" />
            
          </div>
          <select value={filterEstado} onChange={e => setFilterEstado(e.target.value as any)} style={{
          ...inputStyle,
          width: 'auto'
        }} data-id="element-188" data-mp="2zlhzgadvregi">
            
            <option value="todos" data-id="element-189" data-mp="39h35cjm2kyvj">Todos los estados</option>
            <option value="activo" data-id="element-190" data-mp="3eh7pqkq3mdr1">Activos</option>
            <option value="inactivo" data-id="element-191" data-mp="3ehz7osqrwcyu">Inactivos</option>
          </select>
          <button onClick={() => {
          setShowForm(!showForm);
          setEditingId(null);
          setFormData({
            tipo: 'Comercio',
            estado: 'activo',
            capacidadTanque: 1000
          });
        }} style={{
          ...primaryButton,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6
        }} data-id="element-192" data-mp="3ijkt52hdhzkn">
            
            <Plus size={15} data-id="element-193" data-mp="61nvbidoypae" /> Nuevo Cliente
          </button>
        </div>
      </div>

      {/* Formulario */}
      <AnimatePresence data-id="element-194" data-mp="a27vyvxh4b1a">
        {showForm && <motion.div ref={refForm} initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.2
      }} style={{
        overflow: 'hidden'
      }} data-id="element-195" data-mp="a397f1ieizjj">
          
            <div style={{
          ...glass,
          padding: 20
        }} data-id="element-196" data-mp="2fy1vcwgxdn1w">
              <h3 style={{
            fontSize: 15,
            fontWeight: 800,
            marginBottom: 16,
            color: T.text
          }} data-id="element-197" data-mp="2b3nsk8zxodn7">
              
                {editingId ? 'Editar Cliente' : 'Nuevo Cliente'}
              </h3>
              <form onSubmit={handleSubmit} data-id="element-198" data-mp="2yvbf6eq5x2jo">
                <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 14
            }} data-id="element-199" data-mp="2yu9udijch1or">
                
                  <Field label="Código *" data-id="element-200" data-mp="14g0gp0842zau">
                    <input type="text" value={formData.codigo || ''} onChange={e => setFormData({
                  ...formData,
                  codigo: e.target.value
                })} style={inputStyle} placeholder="CLI-001" data-id="element-201" data-mp="14h23u32ejc8x" />
                  
                  </Field>
                  <Field label="Nombre de empresa *" data-id="element-202" data-mp="ubk36di3tua7">
                    <input type="text" value={formData.nombre || ''} onChange={e => setFormData({
                  ...formData,
                  nombre: e.target.value
                })} style={inputStyle} placeholder="Nombre empresa" data-id="element-203" data-mp="gloyhrqalu2a" />
                  
                  </Field>
                  <Field label="RNC" data-id="element-204" data-mp="6plxip76g1gx">
                    <input type="text" value={formData.rnc || ''} onChange={e => setFormData({
                  ...formData,
                  rnc: e.target.value
                })} style={inputStyle} placeholder="123456789" data-id="element-205" data-mp="bmm2ewsaiy6g" />
                  
                  </Field>
                  <Field label="Tipo de cliente" data-id="element-206" data-mp="1s0vh0z6qd8k3">
                    <select value={formData.tipo || 'Comercio'} onChange={e => setFormData({
                  ...formData,
                  tipo: e.target.value as any
                })} style={inputStyle} data-id="element-207" data-mp="1s159ctf8zrsy">
                    
                      <option data-id="element-208" data-mp="35lyqri2hzu37">Industria</option>
                      <option data-id="element-209" data-mp="30m2opodd2cqa">Comercio</option>
                      <option data-id="element-210" data-mp="30ndrbkv2left">Residencial</option>
                      <option data-id="element-211" data-mp="2vt9isx65oycm">Otro</option>
                    </select>
                  </Field>
                  <Field label="Persona de contacto" data-id="element-212" data-mp="2rroa7o0nn688">
                    <input type="text" value={formData.contacto || ''} onChange={e => setFormData({
                  ...formData,
                  contacto: e.target.value
                })} style={inputStyle} placeholder="Nombre contacto" data-id="element-213" data-mp="2rqwnl2p1ciu3" />
                  
                  </Field>
                  <Field label="Propietario / representante legal" data-id="element-214" data-mp="2cxjh7b24y3z9">
                    <input type="text" value={formData.representante || ''} onChange={e => setFormData({
                  ...formData,
                  representante: e.target.value
                })} style={inputStyle} placeholder="Nombre completo (para el contrato)" data-id="element-215" data-mp="28v6k1b1gfee3" />
                  
                  </Field>
                  <Field label="Cédula del representante" data-id="element-216" data-mp="1ozv9cownxpd8">
                    <input type="text" value={formData.representanteCedula || ''} onChange={e => setFormData({
                  ...formData,
                  representanteCedula: e.target.value
                })} style={inputStyle} placeholder="000-0000000-0" data-id="element-217" data-mp="1tyeuwo7cgz52" />
                  
                  </Field>
                  <Field label="Teléfono" data-id="element-218" data-mp="162atu8fsg35a">
                    <input type="tel" value={formData.telefono || ''} onChange={e => setFormData({
                  ...formData,
                  telefono: e.target.value
                })} style={inputStyle} placeholder="+1-809-..." data-id="element-219" data-mp="163lxm898hawe" />
                  
                  </Field>
                  <Field label="Email *" data-id="element-220" data-mp="1k2bx1ujizmnx">
                    <input type="email" value={formData.email || ''} onChange={e => setFormData({
                  ...formData,
                  email: e.target.value
                })} style={inputStyle} placeholder="empresa@email.com" data-id="element-221" data-mp="2qhcpvrd97oqa" />
                  
                  </Field>
                  <Field label="Ubicación" data-id="element-222" data-mp="3ga564ki2f9g1">
                    <input type="text" value={formData.ubicacion || ''} onChange={e => setFormData({
                  ...formData,
                  ubicacion: e.target.value
                })} style={inputStyle} placeholder="Zona/Región" data-id="element-223" data-mp="3g8u2qlsj83pg" />
                  
                  </Field>
                  <Field label="Capacidad tanque (galones)" data-id="element-224" data-mp="2sd7v6uw977tw">
                    <input type="number" value={formData.capacidadTanque || 1000} onChange={e => setFormData({
                  ...formData,
                  capacidadTanque: parseInt(e.target.value)
                })} style={inputStyle} placeholder="1000" data-id="element-225" data-mp="2xb9dveprp54u" />
                  
                  </Field>
                  <Field label="Estado" data-id="element-226" data-mp="hkesqyokksb1">
                    <select value={formData.estado || 'activo'} onChange={e => setFormData({
                  ...formData,
                  estado: e.target.value as any
                })} style={inputStyle} data-id="element-227" data-mp="hjd39p6t439s">
                    
                      <option value="activo" data-id="element-228" data-mp="265vhdwpcs5i5">Activo</option>
                      <option value="inactivo" data-id="element-229" data-mp="2238l9jlnfyf5">Inactivo</option>
                    </select>
                  </Field>
                  <div style={{
                gridColumn: '1 / -1'
              }} data-id="element-230" data-mp="1x9w6s2aop804">
                    <Field label="Dirección" data-id="element-231" data-mp="1sbkxbsoawzif">
                      <input type="text" value={formData.direccion || ''} onChange={e => setFormData({
                    ...formData,
                    direccion: e.target.value
                  })} style={inputStyle} placeholder="Dirección completa" data-id="element-232" data-mp="1nd9o9k5tyt19" />
                    
                    </Field>
                  </div>
                  <div style={{
                gridColumn: '1 / -1'
              }} data-id="element-233" data-mp="19e9zmcibie53">
                    <Field label="Domicilio personal del representante (para el contrato)" data-id="element-234" data-mp="38r5p2li2ttzu">
                      <input type="text" value={formData.representanteDireccion || ''} onChange={e => setFormData({
                    ...formData,
                    representanteDireccion: e.target.value
                  })} style={inputStyle} placeholder="Calle, sector, ciudad" data-id="element-235" data-mp="33rssh91el8k2" />
                    
                    </Field>
                  </div>
                  <div style={{
                gridColumn: '1 / -1'
              }} data-id="element-236" data-mp="3dxasqxhsgoi9">
                    <Field label="Observaciones" data-id="element-237" data-mp="3iv2ibkuynyja">
                      <textarea value={formData.observaciones || ''} onChange={e => setFormData({
                    ...formData,
                    observaciones: e.target.value
                  })} style={{
                    ...inputStyle,
                    resize: 'vertical'
                  }} rows={3} placeholder="Notas adicionales..." data-id="element-238" data-mp="af0rnlvw5vuq" />
                    
                    </Field>
                  </div>
                </div>
                <div style={{
              display: 'flex',
              gap: 8,
              marginTop: 16
            }} data-id="element-239" data-mp="2l57mjigopvvd">
                  <button type="submit" style={primaryButton} data-id="element-240" data-mp="2q3ivzs32i4d2">
                    {editingId ? 'Actualizar' : 'Guardar'} cliente
                  </button>
                  <button type="button" onClick={() => {
                setShowForm(false);
                setEditingId(null);
              }} style={ghostButton} data-id="element-241" data-mp="2dmhup4tx2jjt">
                  
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Lista de Clientes */}
      <div style={{
      ...glass,
      padding: 20
    }} data-id="element-242" data-mp="1uxbhfxbw6k5e">
        <h3 style={{
        fontSize: 15,
        fontWeight: 800,
        marginBottom: 16,
        color: T.text
      }} data-id="element-243" data-mp="1uuys2z3t17ib">
          
          Clientes ({filteredClientes.length})
        </h3>
        {filteredClientes.length === 0 ? <EmptyState icon={<Users size={20} data-id="element-245" data-mp="1g6u7fpl6ylww" />} title="No hay clientes" hint="Registra un cliente nuevo o ajusta los filtros de búsqueda." data-id="element-244" data-mp="1g7dpr7er7cbg" /> : <div style={{
        overflowX: 'auto'
      }} data-id="element-246" data-mp="1b8124kg2617t">
            <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: 13
        }} data-id="element-247" data-mp="1ow1ioejs1ut6">
              <thead data-id="element-248" data-mp="1tvnssf4b6uxr">
                <tr data-id="element-249" data-mp="1tuvzxc23jbb6">
                  {['Código', 'Empresa', 'Tipo', 'Contacto', 'Teléfono', 'Tanque (gal)', 'Estado', 'Acciones'].map((h, i) => <th key={h} style={{
                textAlign: i === 7 ? 'center' : 'left',
                padding: '0 10px 10px',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: T.textMuted,
                borderBottom: `1px solid ${T.border}`
              }} data-id="element-250" data-mp="23kfi0fhzj14z" data-mpi={i}>
                  
                      {h}
                    </th>)}
                </tr>
              </thead>
              <tbody data-id="element-251" data-mp="n65wru91hsid">
                {pageItems.map(cliente => <tr key={cliente.id} data-id="element-252" data-mp="n7h76cwzpyig">
                    <td style={{
                padding: '12px 10px',
                borderBottom: `1px solid ${T.border}`,
                fontFamily: 'monospace',
                color: T.info,
                fontWeight: 700
              }} data-id="element-253" data-mp="n8ii8he0al06">
                  
                      {cliente.codigo}
                    </td>
                    <td style={{
                padding: '12px 10px',
                borderBottom: `1px solid ${T.border}`,
                fontWeight: 600,
                color: T.text
              }} data-id="element-254" data-mp="2wmfa0ld7ygzf">
                  
                      {cliente.nombre}
                    </td>
                    <td style={{
                padding: '12px 10px',
                borderBottom: `1px solid ${T.border}`
              }} data-id="element-255" data-mp="3kc9m30pps1im">
                  
                      <span style={{
                  fontSize: 11,
                  fontWeight: 600,
                  padding: '3px 9px',
                  borderRadius: 99,
                  background: T.surfaceSubtle,
                  color: T.textSecondary
                }} data-id="element-256" data-mp="3ffsx64aoxj4h">
                    
                        {cliente.tipo}
                      </span>
                    </td>
                    <td style={{
                padding: '12px 10px',
                borderBottom: `1px solid ${T.border}`,
                color: T.textSecondary
              }} data-id="element-257" data-mp="20eiwioyg6i7k">
                  
                      {cliente.contacto}
                    </td>
                    <td style={{
                padding: '12px 10px',
                borderBottom: `1px solid ${T.border}`,
                color: T.textSecondary
              }} data-id="element-258" data-mp="1wdp9rywqr9g9">
                  
                      {cliente.telefono}
                    </td>
                    <td style={{
                padding: '12px 10px',
                borderBottom: `1px solid ${T.border}`,
                color: T.text
              }} data-id="element-259" data-mp="18pzbqubv04b2">
                  
                      {cliente.capacidadTanque}
                    </td>
                    <td style={{
                padding: '12px 10px',
                borderBottom: `1px solid ${T.border}`
              }} data-id="element-260" data-mp="ytm8b4qiic34">
                  
                      <Badge label={cliente.estado} color={cliente.estado === 'activo' ? T.ok : T.danger} soft={cliente.estado === 'activo' ? T.okSoft : T.dangerSoft} data-id="element-261" data-mp="k5ci5od2h045" />
                  
                    </td>
                    <td style={{
                padding: '12px 10px',
                borderBottom: `1px solid ${T.border}`,
                textAlign: 'center'
              }} data-id="element-262" data-mp="u4boh0ad0jnz">
                  
                      <div style={{
                  display: 'inline-flex',
                  gap: 6,
                  justifyContent: 'center'
                }} data-id="element-263" data-mp="131ud0en7pg4m">
                    
                        <button onClick={() => handleEdit(cliente)} title="Editar" style={{
                    ...iconBtn,
                    color: T.info
                  }} data-id="element-264" data-mp="1crxaar1d9g8r">
                      
                          <Edit2 size={14} data-id="element-265" data-mp="1lug6isancum1" />
                        </button>
                        <button onClick={() => onDeleteCliente(cliente.id)} title="Eliminar" style={{
                    ...iconBtn,
                    color: T.danger
                  }} data-id="element-266" data-mp="1htctedjzhcoi">
                      
                          <Trash2 size={14} data-id="element-267" data-mp="2t52qnxhn724t" />
                        </button>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
        {filteredClientes.length ? <div style={{
        marginTop: 16
      }} data-id="element-268" data-mp="324pqwfymy1wa">
            <Pagination page={page} onChange={setPage} totalPages={totalPages} rangeStart={rangeStart} rangeEnd={rangeEnd} total={total} label="clientes" data-id="element-269" data-mp="25i2vx0sv6mat" />

          </div> : null}
      </div>
    </div>;
}